import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, Trash2, Pencil, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const WorkAreaEditorModal = ({ open, onOpenChange, onWorkAreasChanged }) => {
  const [workAreas, setWorkAreas] = useState([]);
  const [personalSubAreas, setPersonalSubAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#E5E7EB');
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#E5E7EB');
  const [newSubName, setNewSubName] = useState('');
  const [newSubColor, setNewSubColor] = useState('#E5E7EB');
  const [tab, setTab] = useState('work-areas');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [waRes, psaRes] = await Promise.all([
        fetch(`${API}/org/work-areas`, { credentials: 'include' }),
        fetch(`${API}/org/personal-sub-areas`, { credentials: 'include' })
      ]);
      if (waRes.ok) setWorkAreas(await waRes.json());
      if (psaRes.ok) setPersonalSubAreas(await psaRes.json());
    } catch { toast.error('Failed to load work areas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API}/org/work-areas`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, color: newColor })
      });
      if (res.ok) {
        const area = await res.json();
        setWorkAreas(prev => [...prev, area]);
        setNewName(''); setNewColor('#E5E7EB');
        toast.success(`Added "${name}"`);
        onWorkAreasChanged?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create');
      }
    } catch { toast.error('Failed to create work area'); }
  };

  const handleUpdate = async (workAreaId) => {
    if (!editName.trim()) return;
    try {
      const res = await fetch(`${API}/org/work-areas/${workAreaId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim(), color: editColor })
      });
      if (res.ok) {
        const updated = await res.json();
        setWorkAreas(prev => prev.map(wa => wa.work_area_id === workAreaId ? updated : wa));
        setEditingId(null);
        toast.success('Updated');
        onWorkAreasChanged?.();
      }
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (workAreaId, name) => {
    try {
      const res = await fetch(`${API}/org/work-areas/${workAreaId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) {
        setWorkAreas(prev => prev.filter(wa => wa.work_area_id !== workAreaId));
        toast.success(`Removed "${name}"`);
        onWorkAreasChanged?.();
      }
    } catch { toast.error('Failed to delete'); }
  };

  const handleCreateSubArea = async () => {
    const name = newSubName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API}/org/personal-sub-areas`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, color: newSubColor })
      });
      if (res.ok) {
        const area = await res.json();
        setPersonalSubAreas(prev => [...prev, area]);
        setNewSubName(''); setNewSubColor('#E5E7EB');
        toast.success(`Added "${name}"`);
      }
    } catch { toast.error('Failed to create sub area'); }
  };

  const handleDeleteSubArea = async (subAreaId, name) => {
    try {
      const res = await fetch(`${API}/org/personal-sub-areas/${subAreaId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) {
        setPersonalSubAreas(prev => prev.filter(sa => sa.sub_area_id !== subAreaId));
        toast.success(`Removed "${name}"`);
      }
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="work-area-editor-modal">
        <DialogHeader>
          <DialogTitle>Manage Work Areas</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="work-areas">Work Areas</TabsTrigger>
            <TabsTrigger value="personal-sub">Personal Sub Areas</TabsTrigger>
          </TabsList>

          <TabsContent value="work-areas" className="flex-1 overflow-hidden flex flex-col mt-2">
            {/* Add new */}
            <div className="flex gap-2 pb-2 border-b border orka-border-subtle">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New work area name..."
                className="h-8 text-sm flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                data-testid="new-work-area-input"
              />
              <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="h-8 w-8 rounded cursor-pointer border border orka-border-subtle" />
              <Button size="sm" className="h-8" onClick={handleCreate} data-testid="add-work-area-btn">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto py-2 space-y-1">
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
              ) : workAreas.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No work areas yet</p>
              ) : (
                workAreas.map((wa) => (
                  <div key={wa.work_area_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-stone-50 dark:hover:bg-slate-800 dark:bg-slate-800 group" data-testid={`work-area-row-${wa.work_area_id}`}>
                    {editingId === wa.work_area_id ? (
                      <>
                        <input type="color" value={editColor} onChange={(e) => setEditColor(e.target.value)} className="h-6 w-6 rounded cursor-pointer border" />
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 text-sm flex-1" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleUpdate(wa.work_area_id)} />
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleUpdate(wa.work_area_id)}>
                          <Check className="h-3 w-3 text-green-600" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditingId(null)}>
                          <X className="h-3 w-3 text-slate-400" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: wa.color || '#E5E7EB' }} />
                        <span className="text-sm flex-1 text-slate-800 dark:text-slate-200">{wa.name}</span>
                        {wa.is_personal && <Badge variant="secondary" className="text-[10px] h-4">Personal</Badge>}
                        <Button size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => { setEditingId(wa.work_area_id); setEditName(wa.name); setEditColor(wa.color || '#E5E7EB'); }}>
                          <Pencil className="h-3 w-3 orka-text-tertiary" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => handleDelete(wa.work_area_id, wa.name)}>
                          <Trash2 className="h-3 w-3 text-red-400" />
                        </Button>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="personal-sub" className="flex-1 overflow-hidden flex flex-col mt-2">
            {/* Add new sub area */}
            <div className="flex gap-2 pb-2 border-b border orka-border-subtle">
              <Input
                value={newSubName}
                onChange={(e) => setNewSubName(e.target.value)}
                placeholder="New personal sub area..."
                className="h-8 text-sm flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateSubArea()}
              />
              <input type="color" value={newSubColor} onChange={(e) => setNewSubColor(e.target.value)} className="h-8 w-8 rounded cursor-pointer border border orka-border-subtle" />
              <Button size="sm" className="h-8" onClick={handleCreateSubArea}>
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto py-2 space-y-1">
              {personalSubAreas.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No personal sub areas</p>
              ) : (
                personalSubAreas.map((sa) => (
                  <div key={sa.sub_area_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-stone-50 dark:hover:bg-slate-800 dark:bg-slate-800 group">
                    <span className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: sa.color || '#E5E7EB' }} />
                    <span className="text-sm flex-1 text-slate-800 dark:text-slate-200">{sa.name}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => handleDeleteSubArea(sa.sub_area_id, sa.name)}>
                      <Trash2 className="h-3 w-3 text-red-400" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default WorkAreaEditorModal;
